import React, { createContext, useContext, useState, useEffect } from "react";
import { signin, signup, getMe } from "../api/auth";
import { clearInvite, getStoredInvite } from "../utils/invite";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is logged in on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem("token");
      const savedUser = localStorage.getItem("user");

      if (token && savedUser) {
        try {
          // Verify token is still valid
          const response = await getMe();
          if (response.success) {
            setUser(response.user);
            localStorage.setItem("user", JSON.stringify(response.user));
          } else {
            localStorage.removeItem("token");
            localStorage.removeItem("user");
          }
        } catch (err) {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  // Signed in or out in another tab: what this tab shows belongs to the old account,
  // and its requests already go out as the new one. Start over as whoever it is now.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "token" || e.key === null) window.location.reload();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const login = async (identifier, password) => {
    try {
      setError(null);
      const response = await signin({ identifier, password });
      if (response.success) {
        setUser(response.user);
        localStorage.setItem("token", response.token);
        localStorage.setItem("user", JSON.stringify(response.user));
        return { success: true };
      }
      return { success: false, message: response.message };
    } catch (err) {
      const message =
        err.response?.data?.message || "Login failed. Please try again.";
      setError(message);
      return { success: false, message };
    }
  };

  const register = async (userData) => {
    try {
      setError(null);
      // Came through someone's invite link: they're credited for this sign-up.
      const referralCode = getStoredInvite();
      const response = await signup(referralCode ? { ...userData, referralCode } : userData);
      if (response.success) {
        clearInvite();
        setUser(response.user);
        localStorage.setItem("token", response.token);
        localStorage.setItem("user", JSON.stringify(response.user));
        return { success: true };
      }
      return { success: false, message: response.message };
    } catch (err) {
      const message =
        err.response?.data?.message || "Registration failed. Please try again.";
      setError(message);
      return { success: false, message };
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  };

  const loginWithToken = async (token) => {
    try {
      setError(null);
      localStorage.setItem("token", token);
      const response = await getMe();
      if (response.success) {
        setUser(response.user);
        localStorage.setItem("user", JSON.stringify(response.user));
        return { success: true };
      }
      localStorage.removeItem("token");
      return { success: false, message: "Failed to authenticate" };
    } catch (err) {
      localStorage.removeItem("token");
      const message = err.response?.data?.message || "Authentication failed";
      setError(message);
      return { success: false, message };
    }
  };

  const value = {
    user,
    setUser,
    loading,
    error,
    login,
    register,
    logout,
    loginWithToken,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};






